import { trackDownload } from '../utils/downloadIndicator';
import React, { useState, useEffect, useCallback, useContext } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonIcon from '@mui/icons-material/Person';
import { useTheme } from '@mui/material/styles';
import { leaveRequestsApi, timeEntriesApi, workersApi, tenantApi } from '../services/api';
import { AppContext } from '../App';
import { OfficeLeaveSection } from '../components/LeaveRequests';
import {
  formatTimeEntryDate,
  formatTimeEntryTime,
  getTimeEntryDayKey,
  parseTimeEntryDate,
} from '../utils/timeEntries';

function roundHoursToNearestQuarter(hours) {
  return Math.round(hours * 4) / 4;
}

// Format duration
function formatDuration(hours, { roundToNearestQuarter = false } = {}) {
  if (hours === null || hours === undefined) return '-';
  const displayHours = roundToNearestQuarter ? roundHoursToNearestQuarter(hours) : hours;
  const totalMinutes = Math.round(displayHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
}

const LUNCH_BREAK_MINUTES = 30;
const LUNCH_BREAK_HOURS = LUNCH_BREAK_MINUTES / 60;
const DAILY_HOURS_WARNING_THRESHOLD = 11;
const MAX_EXPORT_RANGE_DAYS = 31;
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getLunchBreakCount(totalHours, workedDayCount) {
  const normalizedWorkedDayCount = Number.isFinite(workedDayCount)
    ? Math.max(workedDayCount, 0)
    : null;

  if (normalizedWorkedDayCount !== null) {
    return normalizedWorkedDayCount;
  }

  if (workedDayCount !== null && workedDayCount !== undefined) {
    return 0;
  }

  return totalHours > 0 ? 1 : 0;
}

function applyLunchBreakDeduction(totalHours, lunchBreakCount = 1) {
  if (totalHours === null || totalHours === undefined) {
    return totalHours;
  }

  return Math.max(totalHours - (lunchBreakCount * LUNCH_BREAK_HOURS), 0);
}

function LunchBreakKey({ sx }) {
  return (
    <Alert 
      severity="info" 
      sx={{ 
        py: 0,
        px: 2,
        alignItems: 'center',
        borderRadius: 2,
        backgroundColor: 'info.50',
        ...sx,
      }}
    >
      <Typography variant="body2">
        Totals shown already include a <strong>30m lunch deduction</strong> for each worked day.
      </Typography>
    </Alert>
  );
}

function addDays(date, days) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

function getStartOfWeek(date) {
  const weekStart = new Date(date);
  weekStart.setHours(0, 0, 0, 0);
  const day = weekStart.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  weekStart.setDate(weekStart.getDate() + offset);
  return weekStart;
}

function getEndOfWeek(date) {
  const weekEnd = addDays(getStartOfWeek(date), 6);
  weekEnd.setHours(23, 59, 59, 999);
  return weekEnd;
}

function getLastFullWeekStart(referenceDate = new Date()) {
  return addDays(getStartOfWeek(referenceDate), -7);
}

function getCurrentWeekStart(referenceDate = new Date()) {
  return getStartOfWeek(referenceDate);
}

function getWeekdayIndex(dateValue) {
  return WEEKDAY_LABELS.indexOf(formatTimeEntryDate(dateValue, { weekday: 'short' }));
}

function formatTime(value) {
  return formatTimeEntryTime(value);
}

function formatWeekLabel(weekStart, weekEnd = getEndOfWeek(weekStart)) {
  return `${weekStart.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })} - ${weekEnd.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })}`;
}

function getEntryStatus(entry) {
  if (entry.is_approved) {
    return { label: 'Approved', color: 'success' };
  }

  if (entry.is_queried) {
    return { label: 'Queried', color: 'error' };
  }

  return { label: 'Pending', color: 'warning' };
}

function getTrimmedText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function getEntryProjectLabel(entry) {
  return getTrimmedText(entry?.project_quote_reference)
    || getTrimmedText(entry?.project_organisation_name)
    || 'Unassigned';
}

function getEntryProjectSecondaryLabel(entry) {
  const projectQuoteReference = getTrimmedText(entry?.project_quote_reference);
  const projectOrganisationName = getTrimmedText(entry?.project_organisation_name);

  if (projectQuoteReference && projectOrganisationName) {
    return projectOrganisationName;
  }

  return '';
}

const NOT_ALLOCATED_PROJECT_SENTINEL_ID = -1;
const NOT_ALLOCATED_PROJECT = {
  project_id: NOT_ALLOCATED_PROJECT_SENTINEL_ID,
  organisation_name: 'Not allocated',
  quote_reference: '',
};

function getProjectOptionLabel(project) {
  if (!project) {
    return '';
  }

  if (project.project_id === NOT_ALLOCATED_PROJECT_SENTINEL_ID) {
    return project.organisation_name || 'Not allocated';
  }

  return getTrimmedText(project.quote_reference)
    || getTrimmedText(project.city)
    || getTrimmedText(project.organisation_name)
    || 'Project';
}

function getProjectOptionSecondaryLabel(project) {
  if (!project || project.project_id === NOT_ALLOCATED_PROJECT_SENTINEL_ID) {
    return '';
  }

  const quoteReference = getTrimmedText(project.quote_reference);
  const organisationName = getTrimmedText(project.organisation_name);

  if (quoteReference && organisationName) {
    return organisationName;
  }

  return '';
}

function getProjectOptions(projects = []) {
  return [NOT_ALLOCATED_PROJECT, ...projects];
}

function getProjectPayloadValue(projectId) {
  if (projectId === '' || projectId === null || projectId === undefined) {
    return {};
  }

  const parsedProjectId = Number(projectId);
  if (Number.isNaN(parsedProjectId)) {
    return {};
  }

  if (parsedProjectId === NOT_ALLOCATED_PROJECT_SENTINEL_ID) {
    return { project_unallocated: true };
  }

  return { project_id: parsedProjectId };
}

function getDateAtStartOfDay(dateValue) {
  const date = new Date(dateValue);
  date.setHours(0, 0, 0, 0);
  return date;
}

function getDateAtEndOfDay(dateValue) {
  const date = new Date(dateValue);
  date.setHours(23, 59, 59, 999);
  return date;
}

function getInclusiveDayCount(startDate, endDate) {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((getDateAtEndOfDay(endDate) - getDateAtStartOfDay(startDate)) / millisecondsPerDay) + 1;
}

function formatExportDate(dateValue) {
  const date = dateValue instanceof Date ? dateValue : parseTimeEntryDate(dateValue);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatExportHours(hours) {
  if (hours === null || hours === undefined) {
    return '';
  }

  return hours.toFixed(2);
}

function buildDailyTotalsExport(entries, workers) {
  const workerNameById = new Map(
    workers.map((worker) => [String(worker.worker_id), worker.name])
  );
  const rowsByWorkerDay = new Map();
  const summaryByWorkerWeek = new Map();

  entries.forEach((entry) => {
    const workerKey = String(entry.worker_id);
    const workerName = workerNameById.get(workerKey) || entry.worker_name || 'Unknown user';
    const entryDate = parseTimeEntryDate(entry.clock_in_time);
    const dayKey = getTimeEntryDayKey(entry.clock_in_time);
    const weekStart = getStartOfWeek(entryDate);
    const rowKey = `${workerKey}-${dayKey}`;
    const summaryKey = `${workerKey}-${weekStart.toISOString()}`;
    const isActiveSession = !entry.clock_out_time;

    if (!rowsByWorkerDay.has(rowKey)) {
      rowsByWorkerDay.set(rowKey, {
        workerName,
        date: entryDate,
        entries: [],
        totalHours: 0,
        projectLabels: new Set(),
      });
    }

    const dayRow = rowsByWorkerDay.get(rowKey);
    dayRow.entries.push(entry);
    dayRow.totalHours += entry.duration || 0;
    dayRow.projectLabels.add(getEntryProjectLabel(entry));

    if (!summaryByWorkerWeek.has(summaryKey)) {
      summaryByWorkerWeek.set(summaryKey, {
        workerName,
        weekStart,
        completedHours: 0,
        completedDayKeys: new Set(),
        activeSessionCount: 0,
      });
    }

    const workerSummary = summaryByWorkerWeek.get(summaryKey);

    if (isActiveSession) {
      workerSummary.activeSessionCount += 1;
      return;
    }

    workerSummary.completedHours += entry.duration || 0;
    workerSummary.completedDayKeys.add(dayKey);
  });

  const exportRows = Array.from(rowsByWorkerDay.values())
    .sort((left, right) => {
      const workerComparison = left.workerName.localeCompare(right.workerName);
      if (workerComparison !== 0) {
        return workerComparison;
      }
      return left.date.getTime() - right.date.getTime();
    })
    .map((row) => {
      const sortedEntries = row.entries
        .slice()
        .sort((left, right) => parseTimeEntryDate(left.clock_in_time).getTime() - parseTimeEntryDate(right.clock_in_time).getTime());
      const adjustedDailyTotal = applyLunchBreakDeduction(row.totalHours, row.entries.length > 0 ? 1 : 0);

      return [
        row.workerName,
        formatExportDate(row.date),
        sortedEntries.map((entry) => formatTime(entry.clock_in_time)).join(', '),
        sortedEntries.map((entry) => (entry.clock_out_time ? formatTime(entry.clock_out_time) : 'Active')).join(', '),
        Array.from(row.projectLabels).join(', '),
        formatExportHours(adjustedDailyTotal),
      ];
    });

  const worksheet = XLSX.utils.aoa_to_sheet([
    ['Note: daily totals include a 30 minute lunch deduction for each worked day.'],
    [],
    ['Name', 'Date', 'Start Times', 'Finish Times', 'Projects', 'Hours Per Day'],
    ...exportRows,
  ]);

  worksheet['!cols'] = [
    { wch: 24 },
    { wch: 14 },
    { wch: 22 },
    { wch: 22 },
    { wch: 26 },
    { wch: 14 },
  ];
  worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];

  const summaryRows = Array.from(summaryByWorkerWeek.values())
    .sort((left, right) => {
      const workerComparison = left.workerName.localeCompare(right.workerName);
      if (workerComparison !== 0) {
        return workerComparison;
      }

      return left.weekStart.getTime() - right.weekStart.getTime();
    })
    .map((row) => {
      const totalHours = applyLunchBreakDeduction(row.completedHours, row.completedDayKeys.size);

      return [
        row.workerName,
        formatExportDate(row.weekStart),
        formatExportHours(totalHours),
        row.activeSessionCount,
      ];
    });

  const summaryWorksheet = XLSX.utils.aoa_to_sheet([
    ['Note: totals here include a 30 minute lunch deduction only for days with completed sessions; active sessions contribute 0 hours.'],
    [],
    ['Name', 'Week Commencing', 'Total Hours', 'Active Sessions'],
    ...summaryRows,
  ]);

  summaryWorksheet['!cols'] = [
    { wch: 24 },
    { wch: 18 },
    { wch: 14 },
    { wch: 16 },
  ];
  summaryWorksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily');
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Weekly');
  return workbook;
}

function DailyTotalsExportDialog({ open, defaultStartDate, defaultEndDate, loading, onClose, onExport }) {
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setStartDate(defaultStartDate);
      setEndDate(defaultEndDate);
      setError(null);
    }
  }, [defaultEndDate, defaultStartDate, open]);

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both a start date and an end date');
      return;
    }

    const normalizedStartDate = getDateAtStartOfDay(startDate);
    const normalizedEndDate = getDateAtEndOfDay(endDate);

    if (normalizedEndDate < normalizedStartDate) {
      setError('End date must be on or after start date');
      return;
    }

    if (getInclusiveDayCount(normalizedStartDate, normalizedEndDate) > MAX_EXPORT_RANGE_DAYS) {
      setError('Exports can cover up to 31 days at a time');
      return;
    }

    setError(null);

    try {
      await onExport({ startDate: normalizedStartDate, endDate: normalizedEndDate });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to export daily totals');
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export Daily Totals</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a date range up to 31 days. Exported daily and weekly totals subtract 30 minutes for each worked day.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleExport} variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <FileDownloadIcon />} disabled={loading}>
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function QueryEntryDialog({ open, entry, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setReason(entry?.query_reason || '');
      setError(null);
      setLoading(false);
    }
  }, [entry, open]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Please provide a query reason');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(entry.time_entry_id, reason.trim());
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to query time entry');
    } finally {
      setLoading(false);
    }
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Query Time Entry</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {entry.worker_name} · {formatTimeEntryDate(entry.clock_in_time, {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </Typography>
          <TextField
            label="Query reason"
            multiline
            rows={4}
            fullWidth
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Explain what needs to be corrected before this entry can be approved."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="warning" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Send Query'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Edit Entry Dialog for Admin
function AdminEditEntryDialog({ open, entry, onClose, onSave, onDelete }) {
  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [notes, setNotes] = useState('');
  const [isApproved, setIsApproved] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [availableProjects, setAvailableProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const projectOptions = getProjectOptions(availableProjects);
  const selectedProject = projectOptions.find(
    (project) => String(project.project_id) === String(selectedProjectId)
  ) || null;

  useEffect(() => {
    if (entry) {
      setClockInTime(parseTimeEntryDate(entry.clock_in_time));
      setClockOutTime(entry.clock_out_time ? parseTimeEntryDate(entry.clock_out_time) : null);
      setNotes(entry.notes || '');
      setIsApproved(entry.is_approved);
      setSelectedProjectId(
        entry.project_id === null || entry.project_id === undefined
          ? String(NOT_ALLOCATED_PROJECT_SENTINEL_ID)
          : String(entry.project_id)
      );
      setError(null);
    }
  }, [entry]);

  useEffect(() => {
    if (!open || !entry?.worker_id) {
      setAvailableProjects([]);
      return;
    }

    let isCurrent = true;
    setProjectsLoading(true);
    workersApi.getAssignedProjects(entry.worker_id)
      .then((response) => {
        if (isCurrent) {
          setAvailableProjects(response.data || []);
        }
      })
      .catch((err) => {
        if (isCurrent) {
          setAvailableProjects([]);
          setError(err?.response?.data?.error || 'Failed to load assigned projects');
        }
      })
      .finally(() => {
        if (isCurrent) {
          setProjectsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [entry?.worker_id, open]);

  useEffect(() => {
    if (
      selectedProjectId === ''
      || Number(selectedProjectId) === NOT_ALLOCATED_PROJECT_SENTINEL_ID
      || projectsLoading
    ) {
      return;
    }

    const selectedProjectStillAssigned = availableProjects.some(
      (project) => String(project.project_id) === String(selectedProjectId)
    );

    if (!selectedProjectStillAssigned) {
      setSelectedProjectId(String(NOT_ALLOCATED_PROJECT_SENTINEL_ID));
    }
  }, [availableProjects, projectsLoading, selectedProjectId]);

  const handleSave = async () => {
    if (clockOutTime && clockOutTime <= clockInTime) {
      setError('Clock out time must be after clock in time');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        clock_in_time: clockInTime.toISOString(),
        notes: notes.trim() || null,
        is_approved: isApproved,
      };

      if (clockOutTime) {
        data.clock_out_time = clockOutTime.toISOString();
      }

      Object.assign(data, getProjectPayloadValue(selectedProjectId));

      await onSave(entry.time_entry_id, data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    
    setLoading(true);
    try {
      await onDelete(entry.time_entry_id);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete entry');
    } finally {
      setLoading(false);
    }
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Time Entry
        <Typography variant="body2" color="text.secondary">
          {entry.worker_name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <DateTimePicker
            label="Clock In Time"
            value={clockInTime}
            onChange={(newValue) => setClockInTime(newValue)}
            slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
          />

          <DateTimePicker
            label="Clock Out Time"
            value={clockOutTime}
            onChange={(newValue) => setClockOutTime(newValue)}
            slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
          />

          <TextField
            label="Notes"
            multiline
            rows={2}
            fullWidth
            margin="normal"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <Autocomplete
            fullWidth
            options={projectOptions}
            value={selectedProject}
            onChange={(_, newValue) => setSelectedProjectId(newValue ? String(newValue.project_id) : '')}
            getOptionLabel={getProjectOptionLabel}
            isOptionEqualToValue={(option, value) => option.project_id === value.project_id}
            loading={projectsLoading}
            disabled={projectsLoading}
            noOptionsText="No matching projects found"
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getProjectOptionLabel(option)}
                  </Typography>
                  {getProjectOptionSecondaryLabel(option) ? (
                    <Typography variant="caption" color="text.secondary">
                      {getProjectOptionSecondaryLabel(option)}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="Project Allocation"
                helperText={projectsLoading
                  ? 'Loading assigned projects...'
                  : 'Search or select one of this team member\'s assigned projects, or choose Not allocated.'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {projectsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={isApproved ? 'approved' : 'pending'}
              label="Status"
              onChange={(e) => setIsApproved(e.target.value === 'approved')}
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={handleDelete} color="error" disabled={loading}>
          Delete
        </Button>
        <Box>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// Pending Approvals Tab
function PendingApprovalsTab({ onUpdate, workerNameDeptMap, selectedDepartment }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [queryEntry, setQueryEntry] = useState(null);
  const [editEntry, setEditEntry] = useState(null);

  const handleEntryClick = (entry) => {
    if (actionLoading) {
      return;
    }

    setQueryEntry(entry);
  };

  const fetchPending = useCallback(async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const response = await timeEntriesApi.getPending();
      setEntries(response.data.entries);
    } catch (err) {
      setError('Failed to load pending entries');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleApprove = async (entryId) => {
    setActionLoading(`approve-${entryId}`);
    try {
      await timeEntriesApi.approve(entryId);
      setEntries((currentEntries) => currentEntries.filter((entry) => entry.time_entry_id !== entryId));
      onUpdate();
    } catch (err) {
      setError('Failed to approve entry');
    } finally {
      setActionLoading(null);
    }
  };

  const handleQuery = async (entryId, reason) => {
    setActionLoading(`query-${entryId}`);
    try {
      const response = await timeEntriesApi.query(entryId, { reason });
      setEntries((currentEntries) => currentEntries.map((entry) => (
        entry.time_entry_id === entryId ? response.data.entry : entry
      )));
      onUpdate();
    } catch (err) {
      setError('Failed to query entry');
      throw err;
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveEntry = async (entryId, data) => {
    setActionLoading(`edit-${entryId}`);
    try {
      const response = await timeEntriesApi.update(entryId, data);
      const updatedEntry = response.data.entry;
      setEntries((currentEntries) => {
        if (updatedEntry?.is_approved) {
          return currentEntries.filter((entry) => entry.time_entry_id !== entryId);
        }

        return currentEntries.map((entry) => (
          entry.time_entry_id === entryId ? updatedEntry : entry
        ));
      });
      onUpdate();
    } catch (err) {
      setError('Failed to update entry');
      throw err;
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    setActionLoading(`delete-${entryId}`);
    try {
      await timeEntriesApi.delete(entryId);
      setEntries((currentEntries) => currentEntries.filter((entry) => entry.time_entry_id !== entryId));
      onUpdate();
    } catch (err) {
      setError('Failed to delete entry');
      throw err;
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const visibleEntries = selectedDepartment
    ? entries.filter(e => !workerNameDeptMap || workerNameDeptMap[e.worker_name] === selectedDepartment)
    : entries;

  if (visibleEntries.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No pending approvals
      </Typography>
    );
  }

  return (
    <>
      <TableContainer>
        <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Clock In</TableCell>
            <TableCell>Clock Out</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Project</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleEntries.map((entry) => {
            const status = getEntryStatus(entry);

            return (
            <TableRow
              key={entry.time_entry_id}
              hover
              onClick={() => handleEntryClick(entry)}
              sx={{
                cursor: actionLoading || entry.is_approved ? 'default' : 'pointer',
              }}
            >
              <TableCell>{entry.worker_name}</TableCell>
              <TableCell>
                {formatTimeEntryDate(entry.clock_in_time, {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </TableCell>
              <TableCell>
                {formatTimeEntryTime(entry.clock_in_time)}
              </TableCell>
              <TableCell>
                {entry.clock_out_time 
                  ? formatTimeEntryTime(entry.clock_out_time)
                  : <Chip label="Active" color="success" size="small" />
                }
              </TableCell>
              <TableCell>{formatDuration(entry.duration)}</TableCell>
              <TableCell>
                <Typography variant="body2">{getEntryProjectLabel(entry)}</Typography>
                {getEntryProjectSecondaryLabel(entry) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {getEntryProjectSecondaryLabel(entry)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={status.label}
                  color={status.color}
                  size="small"
                  sx={{ mb: entry.query_reason ? 0.75 : 0 }}
                />
                {entry.query_reason && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                    {entry.query_reason}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {entry.notes || '-'}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditEntry(entry);
                    }}
                    disabled={Boolean(actionLoading)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    startIcon={actionLoading === `query-${entry.time_entry_id}` ? <CircularProgress size={16} /> : <HelpOutlineIcon />}
                    onClick={(event) => {
                      event.stopPropagation();
                      setQueryEntry(entry);
                    }}
                    disabled={Boolean(actionLoading)}
                  >
                    Query
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    startIcon={actionLoading === `approve-${entry.time_entry_id}` ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleApprove(entry.time_entry_id);
                    }}
                    disabled={Boolean(actionLoading)}
                  >
                    Approve
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          );
          })}
        </TableBody>
        </Table>
      </TableContainer>
      <QueryEntryDialog
        open={Boolean(queryEntry)}
        entry={queryEntry}
        onClose={() => setQueryEntry(null)}
        onSubmit={handleQuery}
      />
      <AdminEditEntryDialog
        open={Boolean(editEntry)}
        entry={editEntry}
        onClose={() => setEditEntry(null)}
        onSave={handleSaveEntry}
        onDelete={handleDeleteEntry}
      />
    </>
  );
}

// Period Totals Tab
function PeriodTotalsTab({ workerDeptMap, selectedDepartment }) {
  const [period, setPeriod] = useState(() => {
    return localStorage.getItem('timesheet_period') || 'weekly';
  });
  const [totals, setTotals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTotals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await timeEntriesApi.getTotals(period, 3);
      setTotals(response.data.periods);
    } catch (err) {
      console.error('Failed to fetch totals:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchTotals();
    localStorage.setItem('timesheet_period', period);
  }, [fetchTotals, period]);

  const formatPeriodLabel = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (period === 'daily') {
      return start.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short' });
    }
    
    return `${start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - ${end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            label="Period"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="biweekly">Biweekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <LunchBreakKey sx={{ mb: 3 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {totals.map((periodData, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {formatPeriodLabel(periodData.start_date, periodData.end_date)}
                  </Typography>
                  {(() => {
                    const filteredWorkers = selectedDepartment && workerDeptMap
                      ? periodData.workers.filter(w => workerDeptMap[w.worker_id] === selectedDepartment)
                      : periodData.workers;
                    const adjustedWorkers = filteredWorkers.map((worker) => {
                      const lunchBreakCount = getLunchBreakCount(worker.total_hours, worker.worked_day_count);

                      return {
                        ...worker,
                        lunchBreakCount,
                        adjustedTotalHours: applyLunchBreakDeduction(worker.total_hours, lunchBreakCount),
                      };
                    });
                    const filteredTotal = adjustedWorkers.reduce(
                      (sum, worker) => sum + (worker.adjustedTotalHours || 0),
                      0
                    );
                    return filteredWorkers.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No entries
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="h4" color="primary" gutterBottom>
                          {formatDuration(filteredTotal, { roundToNearestQuarter: true })}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        {adjustedWorkers.map((worker) => (
                          <Box
                            key={worker.worker_id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 0.5,
                            }}
                          >
                            <Typography variant="body2">
                              {worker.worker_name}
                            </Typography>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body2" fontWeight={500}>
                                {formatDuration(worker.adjustedTotalHours, { roundToNearestQuarter: true })}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

// Daily Totals Tab
function DailyTotalsTab({ allWorkers, selectedDepartment, onUpdate }) {
  const [weekStart, setWeekStart] = useState(() => getLastFullWeekStart());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [queryEntry, setQueryEntry] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const handleEntryClick = (entry) => {
    if (actionLoading) {
      return;
    }

    setQueryEntry(entry);
  };

  const currentWeekStart = getCurrentWeekStart();
  const currentWeekStartTime = currentWeekStart.getTime();
  const isCurrentWeekSelected = weekStart.getTime() === currentWeekStartTime;
  const visibleWeekdayCount = isCurrentWeekSelected ? getWeekdayIndex(new Date()) + 1 : 7;
  const visibleWeekdayLabels = WEEKDAY_LABELS.slice(0, visibleWeekdayCount);
  const displayedWeekEnd = isCurrentWeekSelected ? new Date() : getEndOfWeek(weekStart);
  const filteredWorkers = selectedDepartment
    ? allWorkers.filter((worker) => worker.department === selectedDepartment)
    : allWorkers;
  const filteredWorkerIds = new Set(filteredWorkers.map((worker) => String(worker.worker_id)));

  const fetchWeekEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await timeEntriesApi.getAll({
        start_date: weekStart.toISOString(),
        end_date: (isCurrentWeekSelected ? new Date() : getEndOfWeek(weekStart)).toISOString(),
      });
      setEntries(response.data.entries || []);
    } catch (err) {
      console.error('Failed to fetch daily totals:', err);
      setError('Failed to load daily totals');
    } finally {
      setLoading(false);
    }
  }, [isCurrentWeekSelected, weekStart]);

  useEffect(() => {
    fetchWeekEntries();
  }, [fetchWeekEntries]);

  const rows = (() => {
    const workersById = new Map(
      filteredWorkers.map((worker) => [String(worker.worker_id), worker])
    );
    const groupedEntries = new Map();

    filteredWorkers.forEach((worker) => {
      groupedEntries.set(String(worker.worker_id), {
        worker,
        dayEntries: Array.from({ length: 7 }, () => []),
        dayTotals: Array(7).fill(0),
      });
    });

    entries.forEach((entry) => {
      const workerKey = String(entry.worker_id);
      if (!workersById.has(workerKey)) {
        return;
      }

      const groupedWorker = groupedEntries.get(workerKey);
      const weekdayIndex = getWeekdayIndex(entry.clock_in_time);
      groupedWorker.dayEntries[weekdayIndex].push(entry);
      groupedWorker.dayTotals[weekdayIndex] += entry.duration || 0;
    });

    return Array.from(groupedEntries.values())
      .map(({ worker, dayEntries, dayTotals }) => ({
        worker,
        days: dayEntries.map((items, index) => ({
          entries: items.sort(
            (left, right) => parseTimeEntryDate(left.clock_in_time).getTime() - parseTimeEntryDate(right.clock_in_time).getTime()
          ),
          totalHours: dayTotals[index],
          adjustedTotalHours: applyLunchBreakDeduction(dayTotals[index], items.length > 0 ? 1 : 0),
        })).slice(0, visibleWeekdayCount),
        weeklyTotal: dayTotals.reduce((sum, total) => sum + total, 0),
        weeklyLunchBreakCount: dayEntries.filter((items) => items.length > 0).length,
        adjustedWeeklyTotal: dayTotals.reduce(
          (sum, total, index) => sum + applyLunchBreakDeduction(total, dayEntries[index].length > 0 ? 1 : 0),
          0
        ),
      }))
      .sort((left, right) => left.worker.name.localeCompare(right.worker.name));
  })();

  const handleWeekChange = (value) => {
    if (!value) return;
    const normalizedWeek = getStartOfWeek(value);
    if (normalizedWeek.getTime() > currentWeekStartTime) {
      setWeekStart(currentWeekStart);
      return;
    }
    setWeekStart(normalizedWeek);
  };

  const moveWeek = (offset) => {
    const nextWeek = addDays(weekStart, offset * 7);
    if (nextWeek.getTime() > currentWeekStartTime) {
      setWeekStart(currentWeekStart);
      return;
    }
    setWeekStart(nextWeek);
  };

  const handleApprove = async (entryId) => {
    setActionLoading(`approve-${entryId}`);
    try {
      const response = await timeEntriesApi.approve(entryId);
      setEntries((currentEntries) => currentEntries.map((entry) => (
        entry.time_entry_id === entryId
          ? response.data.entry
          : entry
      )));
      onUpdate();
    } catch (err) {
      setError('Failed to approve entry');
    } finally {
      setActionLoading(null);
    }
  };

  const handleQuery = async (entryId, reason) => {
    setActionLoading(`query-${entryId}`);
    try {
      const response = await timeEntriesApi.query(entryId, { reason });
      setEntries((currentEntries) => currentEntries.map((entry) => (
        entry.time_entry_id === entryId ? response.data.entry : entry
      )));
      onUpdate();
    } catch (err) {
      setError('Failed to query entry');
      throw err;
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = trackDownload(async ({ startDate, endDate }) => {
    setExportLoading(true);

    try {
      const response = await timeEntriesApi.getAll({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      });
      const exportEntries = selectedDepartment
        ? (response.data.entries || []).filter((entry) => filteredWorkerIds.has(String(entry.worker_id)))
        : (response.data.entries || []);

      if (exportEntries.length === 0) {
        throw new Error('No time entries found for the selected date range');
      }

      const workbook = buildDailyTotalsExport(exportEntries, filteredWorkers);
      const exportStartLabel = formatExportDate(startDate).replace(/\//g, '-');
      const exportEndLabel = formatExportDate(endDate).replace(/\//g, '-');
      XLSX.writeFile(workbook, `daily-totals-${exportStartLabel}-to-${exportEndLabel}.xlsx`);
    } finally {
      setExportLoading(false);
    }
  });

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <Button variant="outlined" onClick={() => moveWeek(-1)}>
            Previous Week
          </Button>
          <DatePicker
            label="Week commencing"
            value={weekStart}
            onChange={handleWeekChange}
            maxDate={currentWeekStart}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="outlined"
            onClick={() => moveWeek(1)}
            disabled={weekStart.getTime() >= currentWeekStartTime}
          >
            Next Week
          </Button>
          <Button
            size="small"
            color="warning"
            variant="contained"
            disableElevation
            sx={{
              minWidth: 'auto',
              px: 1.25,
              py: 0.5,
              pointerEvents: 'none',
              textTransform: 'none',
            }}
          >
            Click Entry to Query
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
          >
            Export to Excel
          </Button>
        </Box>

        <Typography variant="subtitle1" fontWeight={600}>
          {formatWeekLabel(weekStart, displayedWeekEnd)}
        </Typography>
      </Box>

      <LunchBreakKey sx={{ mb: 2 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : rows.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No team members available
        </Typography>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: 300 + visibleWeekdayCount * 140 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 180 }}>Name</TableCell>
                {visibleWeekdayLabels.map((label) => (
                  <TableCell key={label} sx={{ minWidth: 140 }}>
                    {label}
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: 120 }}>Weekly Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.worker.worker_id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{row.worker.name}</Typography>
                    {row.worker.department && (
                      <Typography variant="body2" color="text.secondary">
                        {row.worker.department}
                      </Typography>
                    )}
                  </TableCell>

                  {row.days.map((day, index) => {
                    const exceedsThreshold = day.totalHours > DAILY_HOURS_WARNING_THRESHOLD;

                    return (
                      <TableCell
                        key={`${row.worker.worker_id}-${visibleWeekdayLabels[index]}`}
                        sx={{
                          verticalAlign: 'top',
                          backgroundColor: exceedsThreshold ? 'warning.light' : 'inherit',
                        }}
                      >
                        {day.entries.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        ) : (
                          <Box>
                             {day.entries.map((entry) => {
                               const status = getEntryStatus(entry);

                               return (
                               <Box
                                 key={entry.time_entry_id}
                                 onClick={() => handleEntryClick(entry)}
                                 sx={{
                                   mb: 0.75,
                                   cursor: actionLoading ? 'default' : 'pointer',
                                   borderRadius: 1,
                                   px: 0.75,
                                   py: 0.5,
                                   '&:hover': actionLoading ? undefined : {
                                     backgroundColor: 'action.hover',
                                   },
                                 }}
                               >
                                 <Typography variant="body2">
                                   {formatTime(entry.clock_in_time)} - {formatTime(entry.clock_out_time)}
                                 </Typography>
                                 <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                   {getEntryProjectLabel(entry)}
                                 </Typography>
                                 {getEntryProjectSecondaryLabel(entry) && (
                                   <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                     {getEntryProjectSecondaryLabel(entry)}
                                   </Typography>
                                 )}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                    <Chip
                                      label={status.label}
                                      color={status.color}
                                      size="small"
                                    />
                                   {!entry.is_approved && (
                                     <>
                                       <Button
                                         size="small"
                                         color="warning"
                                         variant="outlined"
                                         startIcon={actionLoading === `query-${entry.time_entry_id}` ? <CircularProgress size={14} /> : <HelpOutlineIcon />}
                                         onClick={(event) => {
                                           event.stopPropagation();
                                           setQueryEntry(entry);
                                         }}
                                         disabled={Boolean(actionLoading)}
                                       >
                                         Query
                                       </Button>
                                       <Button
                                         size="small"
                                         color="success"
                                         variant="contained"
                                         startIcon={actionLoading === `approve-${entry.time_entry_id}` ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                                         onClick={(event) => {
                                           event.stopPropagation();
                                           handleApprove(entry.time_entry_id);
                                         }}
                                         disabled={Boolean(actionLoading)}
                                       >
                                         Approve
                                       </Button>
                                     </>
                                   )}
                                 </Box>
                                  {entry.query_reason && (
                                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                                      Query: {entry.query_reason}
                                    </Typography>
                                  )}
                                </Box>
                              );
                              })}
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.75,
                                fontWeight: 600,
                                color: exceedsThreshold ? 'warning.dark' : 'text.secondary',
                              }}
                            >
                              {formatDuration(day.adjustedTotalHours)}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}

                    <TableCell>
                      <Typography fontWeight={700} color="primary">
                        {formatDuration(row.adjustedWeeklyTotal, { roundToNearestQuarter: true })}
                      </Typography>
                    </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <QueryEntryDialog
        open={Boolean(queryEntry)}
        entry={queryEntry}
        onClose={() => setQueryEntry(null)}
        onSubmit={handleQuery}
      />
      <DailyTotalsExportDialog
        open={exportDialogOpen}
        defaultStartDate={weekStart}
        defaultEndDate={displayedWeekEnd}
        loading={exportLoading}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExport}
      />
    </Box>
  );
}

// History Tab
function WorkerHistoryTab({ allWorkers, selectedDepartment }) {
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [entries, setEntries] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  const filteredWorkers = selectedDepartment
    ? allWorkers.filter(w => w.department === selectedDepartment)
    : allWorkers;

  // Clear worker selection when department filter changes
  useEffect(() => {
    setSelectedWorker(null);
    setEntries([]);
  }, [selectedDepartment]);

  const fetchEntries = useCallback(async () => {
    if (!selectedWorker) return;
    
    setLoading(true);
    try {
      const response = await timeEntriesApi.getWorkerEntries(
        selectedWorker,
        startDate?.toISOString(),
        endDate?.toISOString()
      );
      setEntries(response.data.entries);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedWorker, startDate, endDate]);

  useEffect(() => {
    if (selectedWorker) {
      fetchEntries();
    }
  }, [selectedWorker, fetchEntries]);

  const handleSaveEntry = async (entryId, data) => {
    await timeEntriesApi.update(entryId, data);
    setAlert({ show: true, message: 'Entry updated successfully', severity: 'success' });
    fetchEntries();
  };

  const handleDeleteEntry = async (entryId) => {
    await timeEntriesApi.delete(entryId);
    setAlert({ show: true, message: 'Entry deleted successfully', severity: 'success' });
    fetchEntries();
  };

  // Group entries by date
  const entriesByDate = entries.reduce((acc, entry) => {
    const dateKey = getTimeEntryDayKey(entry.clock_in_time);
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(entry);
    return acc;
  }, {});

  return (
    <Box>
      {alert.show && (
        <Alert 
          severity={alert.severity} 
          onClose={() => setAlert({ ...alert, show: false })}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Name</InputLabel>
            <Select
              value={selectedWorker || ''}
              label="Name"
              onChange={(e) => setSelectedWorker(e.target.value)}
              SelectDisplayProps={{ 'aria-label': 'Name' }}
            >
              {filteredWorkers.map((worker) => (
                <MenuItem key={worker.worker_id} value={worker.worker_id}>
                  {worker.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button 
            variant="contained" 
            fullWidth 
            sx={{ height: '100%' }}
            onClick={fetchEntries}
            disabled={!selectedWorker}
          >
            Search
          </Button>
        </Grid>
      </Grid>

      <LunchBreakKey sx={{ mb: 2 }} />

      {!selectedWorker ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Select a team member to view their time entries
        </Typography>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : entries.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No entries found
        </Typography>
      ) : (
        Object.entries(entriesByDate)
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([dateKey, dateEntries]) => {
            const dailyTotal = dateEntries.reduce((sum, e) => sum + (e.duration || 0), 0);
            const adjustedDailyTotal = applyLunchBreakDeduction(dailyTotal, dateEntries.length > 0 ? 1 : 0);

            return (
              <Accordion key={dateKey} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2, gap: 2 }}>
                    <Typography>
                      {formatTimeEntryDate(dateEntries[0].clock_in_time, {
                        weekday: 'long',
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography color="primary" fontWeight={600}>
                        {formatDuration(adjustedDailyTotal)}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Clock In</TableCell>
                        <TableCell>Clock Out</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Project</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dateEntries.map((entry) => (
                        <TableRow key={entry.time_entry_id}>
                          <TableCell>
                            {formatTimeEntryTime(entry.clock_in_time)}
                          </TableCell>
                          <TableCell>
                            {entry.clock_out_time 
                              ? formatTimeEntryTime(entry.clock_out_time)
                              : <Chip label="Active" color="success" size="small" />
                            }
                          </TableCell>
                          <TableCell>{formatDuration(entry.duration)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{getEntryProjectLabel(entry)}</Typography>
                            {getEntryProjectSecondaryLabel(entry) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {getEntryProjectSecondaryLabel(entry)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {entry.is_approved ? (
                              <Chip label="Approved" color="success" size="small" />
                            ) : (
                              <Chip label="Pending" color="warning" size="small" />
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {entry.notes || '-'}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setSelectedEntry(entry);
                                setEditDialogOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            );
          })
      )}

      <AdminEditEntryDialog
        open={editDialogOpen}
        entry={selectedEntry}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedEntry(null);
        }}
        onSave={handleSaveEntry}
        onDelete={handleDeleteEntry}
      />
    </Box>
  );
}

export default function TimeSheets() {
  const { currentUser } = useContext(AppContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [allWorkers, setAllWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');

  // Load all workers for history and department-aware filtering.
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const response = await workersApi.getAll();
        setAllWorkers(response.data || []);
      } catch (err) {
        console.error('Failed to fetch workers:', err);
      }
    };
    fetchWorkers();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await tenantApi.getBranding();
        const tenantDepartments = response.data?.branding?.departments;

        if (Array.isArray(tenantDepartments) && tenantDepartments.length > 0) {
          setDepartments(tenantDepartments);
          if (currentUser?.department && tenantDepartments.includes(currentUser.department)) {
            setSelectedDepartment(currentUser.department);
          } else {
            setSelectedDepartment('');
          }
        } else {
          setDepartments([]);
          setSelectedDepartment('');
        }
      } catch (err) {
        console.error('Failed to fetch departments:', err);
        setDepartments([]);
        setSelectedDepartment('');
      }
    };

    fetchDepartments();
  }, [currentUser?.department]);

  // Build maps for filtering: by worker_id and by worker name
  const workerDeptMap = {};
  const workerNameDeptMap = {};
  allWorkers.forEach(w => {
    if (w.worker_id) workerDeptMap[w.worker_id] = w.department;
    if (w.name) workerNameDeptMap[w.name] = w.department;
  });

  const fetchPendingCount = useCallback(async () => {
    try {
      const response = await timeEntriesApi.getPending();
      setPendingCount(response.data.count);
    } catch (err) {
      console.error('Failed to fetch pending count:', err);
    }
  }, []);

  const fetchPendingLeaveCount = useCallback(async () => {
    try {
      const response = await leaveRequestsApi.getAll({ status: 'pending' });
      setPendingLeaveCount(response.data.count);
    } catch (err) {
      console.error('Failed to fetch pending leave count:', err);
    }
  }, []);

  useEffect(() => {
    fetchPendingCount();
    fetchPendingLeaveCount();
  }, [fetchPendingCount, fetchPendingLeaveCount, tabValue]);

  const hasDepartments = departments.length > 0;
  const pendingTabLabel = isSmallScreen ? 'Pending' : 'Pending Approvals';
  const dailyTotalsTabLabel = isSmallScreen ? 'Daily' : 'Daily Totals';
  const periodTotalsTabLabel = isSmallScreen ? 'Periods' : 'Period Totals';
  const workerHistoryTabLabel = 'History';

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccessTimeIcon fontSize="large" />
        Time Sheets
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
            mb: 3,
          }}
        >
          {hasDepartments && (
            <FormControl
              sx={{
                minWidth: { xs: '100%', md: 200 },
                width: { xs: '100%', md: 'auto' },
              }}
              size="small"
            >
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                label="Department"
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons="auto"
            sx={{
              flex: 1,
              width: '100%',
              minWidth: 0,
              '& .MuiTabs-flexContainer': {
                gap: { xs: 0.5, sm: 1 },
              },
              '& .MuiTab-root': {
                minHeight: { xs: 44, sm: 48 },
                minWidth: { xs: 104, sm: 140 },
                px: { xs: 1.5, sm: 2 },
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  {pendingTabLabel}
                  {pendingCount > 0 && (
                    <Chip label={pendingCount} color="warning" size="small" />
                  )}
                </Box>
              }
            />
            <Tab label={dailyTotalsTabLabel} />
            <Tab label={periodTotalsTabLabel} />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  {!isSmallScreen && <PersonIcon fontSize="small" />}
                  {workerHistoryTabLabel}
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  {isSmallScreen ? 'Leave' : 'Holiday & Leave'}
                  {pendingLeaveCount > 0 && (
                    <Chip label={pendingLeaveCount} color="warning" size="small" />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <PendingApprovalsTab
            onUpdate={fetchPendingCount}
            workerNameDeptMap={workerNameDeptMap}
            selectedDepartment={selectedDepartment}
          />
        )}
        {tabValue === 1 && (
          <DailyTotalsTab
            allWorkers={allWorkers}
            selectedDepartment={selectedDepartment}
            onUpdate={fetchPendingCount}
          />
        )}
        {tabValue === 2 && (
          <PeriodTotalsTab
            workerDeptMap={workerDeptMap}
            selectedDepartment={selectedDepartment}
          />
        )}
        {tabValue === 3 && (
          <WorkerHistoryTab
            allWorkers={allWorkers}
            selectedDepartment={selectedDepartment}
          />
        )}
        {tabValue === 4 && (
          <OfficeLeaveSection
            workerDeptMap={workerDeptMap}
            selectedDepartment={selectedDepartment}
            onUpdate={fetchPendingLeaveCount}
          />
        )}
      </Paper>
    </Container>
  );
}
